import type { NavLinkProps } from '@remix-run/react';
import { NavLink } from '@remix-run/react';
import { cn } from '@cardo/lib';

type StyleType =
  | 'default'
  | 'black-and-blue'
  | 'black-and-violet'
  | 'black-and-green'
  | 'rounded-blue'
  | 'rounded-violet'
  | 'rounded-green';

const getBaseStyles = (styleType: StyleType = 'default') => {
  switch (styleType) {
    case 'rounded-blue':
      return {
        base: 'flex items-center text-base rounded-full border-2 px-3 py-1 no-underline',
        active: 'text-white bg-blue-600 border-blue-600',
        inactive:
          'text-blue-600 border-blue-600 hover:text-blue-600 hover:border-blue-600',
      };
    case 'rounded-violet':
      return {
        base: 'flex items-center text-base rounded-full border-2 px-3 py-1 no-underline',
        active: 'text-white bg-violet-600 border-violet-600',
        inactive:
          'text-violet-600 border-violet-600 hover:text-violet-600 hover:border-violet-600',
      };
    case 'rounded-green':
      return {
        base: 'flex items-center text-base rounded-full border-2 px-3 py-1 no-underline',
        active: 'text-white bg-green-600 border-green-600',
        inactive:
          'text-green-600 border-green-600 hover:text-green-600 hover:border-green-600',
      };
    case 'black-and-blue':
      return {
        base: 'flex items-center text-xl font-bold border-b-4 pb-[4px] sm:pb-[12px]',
        active: 'text-black no-underline border-b-blue-600',
        inactive: 'no-underline border-b-transparent hover:border-b-blue-600 ',
      };
    case 'black-and-violet':
      return {
        base: 'flex items-center text-xl font-bold border-b-4 pb-[4px] sm:pb-[12px]',
        active: 'text-black no-underline border-b-violet-600',
        inactive:
          'no-underline border-b-transparent hover:border-b-violet-600 ',
      };
    case 'black-and-green':
      return {
        base: 'flex items-center text-xl font-bold border-b-4 pb-[4px] sm:pb-[12px]',
        active: 'text-black no-underline border-b-green-600',
        inactive: 'no-underline border-b-transparent hover:border-b-green-600 ',
      };
    default:
      return {
        base: 'flex items-center text-xl font-bold border-b-4 pb-[4px] sm:pb-[12px]',
        active: 'text-white no-underline border-b-theme-cyan-light',
        inactive:
          'no-underline border-b-transparent hover:border-b-theme-cyan-light hover:text-white',
      };
  }
};

const getInactiveStateStyle = (
  styleType: StyleType = 'default',
  isSubNavLink: boolean = false,
) => {
  switch (styleType) {
    case 'rounded-blue':
      return 'text-blue-600';

    case 'rounded-violet':
      return 'text-violet-600';

    case 'rounded-green':
      return 'text-green-600';

    case 'black-and-blue':
      return 'text-black';

    case 'black-and-violet':
      return 'text-black';

    case 'black-and-green':
      return 'text-black';

    default:
      return isSubNavLink ? 'text-white' : 'text-slate-400';
  }
};

export default function PageNavLink({
  to,
  className,
  children,
  icon,
  isSubNavLink,
  styleType,
  ...props
}: NavLinkProps & {
  icon?: React.ReactNode;
  isSubNavLink?: boolean;
  styleType?: StyleType;
  children: React.ReactNode;
}) {
  if (!to) return null;
  const styles = getBaseStyles(styleType);
  const inactiveStateStyle = getInactiveStateStyle(styleType, isSubNavLink);

  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          styles.base,
          isActive ? styles.active : styles.inactive,
          !isActive && inactiveStateStyle,
          className,
        )
      }
      prefetch="intent"
      {...props}
    >
      {icon && <span className="mr-1.5">{icon}</span>}
      <span className="whitespace-nowrap">{children}</span>
    </NavLink>
  );
}
